import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ApiError, cardComplete, cardPresign, fmtBytes,
  mpuAbort, mpuComplete, mpuSign, mpuStart,
  MPU_THRESHOLD, PART_SIZE,
  type CardPresignItem,
} from '../api'
import {
  CardReadError, breathe, collect, configFacts, fromPicker, hash, hashParts,
  scan, type CardScan, type Part,
} from '../cards'

/** How many small files are presigned and confirmed together. Matches the
 *  receiver's own limit, which matches the node upload contract. */
const BATCH = 32

/** Concurrent PUTs of small files. Object storage is nowhere near the limit
 *  here; a laptop on a hotel connection is. */
const PARALLEL = 4

/** Concurrent parts within one large file. Three keeps a domestic uplink full
 *  without making the per-file progress meaningless, and bounds what has to be
 *  re-sent when the connection drops. */
const PARALLEL_PARTS = 3

/** Attempts per part before the file is called failed. A part is 64 MiB; a
 *  retry is cheap and a transient 500 from object storage is common enough
 *  that failing the whole file on the first one would be wrong. */
const PART_TRIES = 3

/** Rows kept in the DOM. Anything unfinished or failed is always shown; the
 *  cap only ever hides files that are safely stored. */
const MAX_ROWS = 300

type Phase = 'idle' | 'scanning' | 'blocked' | 'uploading' | 'done'

type Status = 'queued' | 'checking' | 'sending' | 'stored' | 'already' | 'failed'

interface Row {
  name: string
  size: number
  kind: 'audio' | 'reference'
  file: File
  startUtc: string
  status: Status
  /** Bytes read (while checking) or sent (while sending). Drives the row's own
   *  bar, which for a 4 GiB file is the difference between an import that is
   *  working and one that has hung. */
  done: number
  /** Bytes per second, measured over the upload so far. */
  rate?: number
  detail?: string
  conflict?: boolean
}

/** Import a recorder's memory card.
 *
 *  **It starts on drop.** There is no import button: dropping a card is not an
 *  ambiguous act, and the confirmation step it used to need sat below a
 *  summary long enough to scroll off the screen — so the thing you had to
 *  press to make anything happen was the one thing you could not see.
 *
 *  **Every file says what is happening to it, in bytes.** A card off a
 *  continuously-recording unit holds files of four gigabytes, which is minutes
 *  of checksumming and then tens of minutes of uploading. At that scale a
 *  status word is not feedback — "waiting" on a row that will not move for
 *  half an hour tells you nothing about whether anything is wrong. Each row
 *  carries its own progress and rate, and the summary counts bytes rather than
 *  files, because with files this size "3 of 18" is not a measure of anything.
 *
 *  **Large files go up in parts.** One PUT of four gigabytes is a single
 *  connection that has to survive the whole upload, and when it does not, the
 *  file starts again from nothing. Above `MPU_THRESHOLD` the file is uploaded
 *  as 64 MiB parts, each retried on its own.
 *
 *  The receiver never sees the bytes (R7.2): each file is hashed here, a
 *  presigned PUT is fetched for it, and the browser writes straight to object
 *  storage — the same data plane a node uses, reached through the other door.
 */
export function CardImport({
  missionId, recorderSerial, onImported,
}: {
  missionId: string
  /** What the deployment says is in the water, for the check against the
   *  serial the card names. Absent is normal for older registrations. */
  recorderSerial?: string | null
  onImported: () => void
}) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [over, setOver] = useState(false)
  const [card, setCard] = useState<CardScan | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const cancelled = useRef(false)
  const live = useRef(new Set<XMLHttpRequest>())
  const picker = useRef<HTMLInputElement>(null)

  // The rows are mutated in place and the list is repainted on a timer while
  // an import runs. A 4 GiB file reports progress hundreds of times, and one
  // React render each is how a progress list becomes the reason the page is
  // slow. The array is the truth; `tick` only says when to look at it.
  const rowsRef = useRef<Row[]>([])
  const [, setTick] = useState(0)
  const repaint = () => setTick((n) => n + 1)

  useEffect(() => {
    if (phase !== 'uploading') return
    const h = window.setInterval(repaint, 150)
    return () => window.clearInterval(h)
  }, [phase])

  const stop = useCallback(() => {
    cancelled.current = true
    for (const x of live.current) { try { x.abort() } catch { /* gone */ } }
    live.current.clear()
  }, [])

  useEffect(() => () => stop(), [stop])

  /* ------------------------------------------------------------- one PUT */

  /** A PUT that reports its progress and can be aborted.
   *
   *  `fetch` cannot do either. It has no upload progress event at all, so a
   *  four-gigabyte body is a promise that settles half an hour later with
   *  nothing in between, and that is exactly the case this screen exists to
   *  show. XHR is the older API and the one that answers the question. */
  const put = useCallback((
    url: string, body: Blob, headers: Record<string, string>,
    onProgress?: (loaded: number) => void,
  ) => new Promise<{ status: number; etag: string | null }>((resolve, reject) => {
    const x = new XMLHttpRequest()
    live.current.add(x)
    x.open('PUT', url, true)
    for (const [k, v] of Object.entries(headers)) x.setRequestHeader(k, v)
    if (onProgress) x.upload.onprogress = (e) => onProgress(e.loaded)
    x.onload = () => {
      live.current.delete(x)
      // Readable only if the bucket exposes it via CORS. The part checksums
      // are computed locally for exactly that reason, so this is a preference
      // rather than a dependency.
      let etag: string | null = null
      try { etag = x.getResponseHeader('ETag') } catch { /* not exposed */ }
      resolve({ status: x.status, etag })
    }
    x.onerror = () => { live.current.delete(x); reject(new Error('the connection dropped')) }
    x.onabort = () => { live.current.delete(x); reject(new Error('stopped')) }
    x.send(body)
  }), [])

  /* ------------------------------------------------- one large file, in parts */

  const sendLarge = useCallback(async (row: Row): Promise<'stored' | 'already' | 'failed'> => {
    row.status = 'checking'; row.done = 0; repaint()
    let hashed: { md5B64: string; parts: Part[] }
    try {
      hashed = await hashParts(row.file, PART_SIZE, (d) => { row.done = d })
    } catch (e) {
      // Where it stopped, not just that it did. On a four-gigabyte file
      // "could not be read" is the same sentence whether the card went to
      // sleep two seconds in or the last window of the file is bad, and those
      // want different things done about them.
      row.detail = e instanceof CardReadError
        ? `card stopped answering at ${fmtBytes(e.at)} of ${fmtBytes(e.total)}`
          + ' — drop the folder again to retry this file'
        : e instanceof Error ? `could not be read — ${e.message}`
        : 'could not be read from the card'
      return 'failed'
    }
    if (cancelled.current) return 'failed'

    const item: CardPresignItem = {
      kind: row.kind, name: row.name, start_utc: row.startUtc,
      size: row.size, md5_b64: hashed.md5B64,
    }
    let started
    try {
      started = await mpuStart(missionId, item, PART_SIZE)
    } catch (e) {
      row.detail = e instanceof ApiError ? (e.detail ?? e.code) : 'the receiver refused it'
      return 'failed'
    }
    if (started.duplicate) return 'already'
    if (started.attribution_conflict) row.conflict = true
    const uploadId = started.upload_id!
    const key = started.key

    row.status = 'sending'; row.done = 0; repaint()
    const began = performance.now()
    let settled = 0
    const flight = new Map<number, number>()
    const measure = () => {
      let n = settled
      for (const v of flight.values()) n += v
      row.done = n
      const secs = (performance.now() - began) / 1000
      if (secs > 1) row.rate = n / secs
    }

    // Signed a batch at a time, because the URLs expire and a 4 GiB file is 64
    // parts. Uploaded a few at a time, so a dropped connection costs one part.
    try {
      for (let i = 0; i < hashed.parts.length; i += BATCH) {
        if (cancelled.current) throw new Error('stopped')
        const chunk = hashed.parts.slice(i, i + BATCH)
        const signed = await mpuSign(missionId, key, uploadId,
          chunk.map((p) => ({ part_number: p.n, md5_b64: p.md5B64 })))
        const byNumber = new Map(signed.parts.map((sp) => [sp.part_number, sp]))

        for (let j = 0; j < chunk.length; j += PARALLEL_PARTS) {
          if (cancelled.current) throw new Error('stopped')
          await Promise.all(chunk.slice(j, j + PARALLEL_PARTS).map(async (part) => {
            const sp = byNumber.get(part.n)
            if (!sp) throw new Error(`part ${part.n} was not signed`)
            const body = row.file.slice(part.offset, part.offset + part.size)
            for (let attempt = 1; ; attempt++) {
              try {
                const res = await put(sp.url, body, sp.headers, (loaded) => {
                  flight.set(part.n, loaded); measure()
                })
                if (res.status >= 200 && res.status < 300) {
                  flight.delete(part.n); settled += part.size; measure()
                  return
                }
                throw new Error(`storage refused part ${part.n} (${res.status})`)
              } catch (e) {
                flight.delete(part.n); measure()
                if (cancelled.current) throw new Error('stopped')
                if (attempt >= PART_TRIES) throw e
                // Backing off rather than hammering: a 500 from object storage
                // is usually a moment, not a state.
                await new Promise((r) => setTimeout(r, 400 * attempt))
              }
            }
          }))
          repaint()
        }
      }

      await mpuComplete(missionId, key, uploadId,
        hashed.parts.map((p) => ({ part_number: p.n, etag: `"${p.etag}"` })))
      return 'stored'
    } catch (e) {
      // Parts already sent are billed until the upload is abandoned, and an
      // operator who pressed stop has no other way to say so.
      try { await mpuAbort(missionId, key, uploadId) } catch { /* best effort */ }
      row.detail = cancelled.current ? 'stopped'
        : e instanceof ApiError ? (e.detail ?? e.code)
        : e instanceof Error ? e.message : 'the upload did not complete'
      return 'failed'
    }
  }, [missionId, put])

  /* ------------------------------------------- a batch of ordinary files */

  const sendSmall = useCallback(async (batch: Row[]) => {
    const items: { row: Row; it: CardPresignItem }[] = []
    for (const row of batch) {
      if (cancelled.current) break
      row.status = 'checking'; row.done = 0
      try {
        const md5_b64 = await hash(row.file, (d) => { row.done = d })
        items.push({ row, it: {
          kind: row.kind, name: row.name, start_utc: row.startUtc,
          size: row.size, md5_b64,
        } })
        row.status = 'queued'
      } catch (e) {
        // Per file, not per batch. One unreadable file used to fail all
        // thirty-two of its neighbours and throw the reason away.
        row.status = 'failed'
        row.detail = e instanceof Error ? `could not be read — ${e.message}`
                                        : 'could not be read from the card'
      }
      await breathe()
    }
    repaint()
    if (!items.length) return true

    let signed
    try {
      signed = await cardPresign(missionId, items.map((x) => x.it))
    } catch (e) {
      const why = e instanceof ApiError ? (e.detail ?? e.code)
                                        : 'the receiver refused the batch'
      for (const x of items) { x.row.status = 'failed'; x.row.detail = why }
      setErr(`${why} — nothing further was uploaded.`)
      return false
    }

    for (const r of signed.rejected ?? []) {
      const row = items[r.index]?.row
      if (!row) continue
      if (r.reason === 'duplicate') row.status = 'already'
      else { row.status = 'failed'; row.detail = r.detail ?? r.reason }
    }

    const jobs = signed.urls
      .map((u, n) => (u ? { u, row: items[n]!.row } : null))
      .filter((x): x is NonNullable<typeof x> => x !== null)
    for (const j of jobs) if (j.u.attribution_conflict) j.row.conflict = true

    const landed: string[] = []
    for (let k = 0; k < jobs.length; k += PARALLEL) {
      if (cancelled.current) break
      await Promise.all(jobs.slice(k, k + PARALLEL).map(async (job) => {
        job.row.status = 'sending'; job.row.done = 0
        try {
          const res = await put(job.u.url, job.row.file, job.u.headers,
                                (loaded) => { job.row.done = loaded })
          if (res.status >= 200 && res.status < 300) {
            landed.push(job.u.key); job.row.status = 'stored'
            job.row.done = job.row.size; return
          }
          if (res.status === 412) {
            // The precondition on raw/ did its job: this key is already the
            // archive of record. A retry, not a collision.
            job.row.status = 'already'; return
          }
          job.row.status = 'failed'
          job.row.detail = `storage refused it (${res.status})`
        } catch (e) {
          job.row.status = 'failed'
          job.row.detail = e instanceof Error ? e.message : 'the upload did not complete'
        }
      }))
      repaint()
    }

    if (landed.length) {
      try {
        await cardComplete(missionId, landed)
      } catch {
        // The bytes are in the bucket; only the confirmation was lost. Those
        // rows stay pending and the next run over the same card resolves them.
        setNote('Some uploads could not be confirmed. Drop the same folders '
              + 'again to finish them — nothing is lost.')
      }
    }
    repaint()
    return true
  }, [missionId, put])

  /* ------------------------------------------------------------ the loop */

  const upload = useCallback(async (rows: Row[]) => {
    cancelled.current = false
    setPhase('uploading'); setErr(null)
    let announced = false
    const announce = () => { if (!announced) { announced = true; onImported() } }

    // Small files are batched; a large one interrupts the batch and goes on
    // its own. Order is preserved, so the config file is still first and the
    // recordings still arrive oldest-first.
    let batch: Row[] = []
    const flush = async () => {
      if (!batch.length) return true
      const ok = await sendSmall(batch)
      batch = []
      announce()
      return ok
    }

    for (const row of rows) {
      if (cancelled.current) break
      if (row.size <= MPU_THRESHOLD) {
        batch.push(row)
        if (batch.length >= BATCH && !(await flush())) { setPhase('done'); return }
        continue
      }
      if (!(await flush())) { setPhase('done'); return }
      if (cancelled.current) break
      const outcome = await sendLarge(row)
      row.status = outcome
      if (outcome === 'stored') row.done = row.size
      announce()
      repaint()
    }
    if (!cancelled.current) await flush()

    for (const row of rows) {
      if (row.status === 'checking' || row.status === 'sending' || row.status === 'queued') {
        row.status = 'failed'
        row.detail = row.detail ?? 'stopped'
      }
    }

    setPhase('done'); repaint()
    onImported()
  }, [onImported, sendSmall, sendLarge])

  /* -------------------------------------------------------------- reading */

  const take = useCallback(async (found: Awaited<ReturnType<typeof collect>>) => {
    setPhase('scanning'); setErr(null); setNote(null)
    rowsRef.current = []; repaint()
    let s: CardScan
    try {
      s = await scan(found)
    } catch (e) {
      setPhase('idle')
      setErr(e instanceof Error ? e.message : 'Could not read what was dropped.')
      return
    }
    setCard(s)

    if (!s.wavs.length) {
      setPhase('idle')
      setErr('Nothing on this drop looked like a recording. Drop the card’s '
           + 'YYYYMMDD folders, or the card itself.')
      return
    }

    // The config file rides along as a `reference` object on the same
    // deployment, timestamped at the first recording rather than at now: it
    // describes the card, so filing it under the day of the import would put
    // it months away from everything it describes.
    const firstAt = s.wavs[0]!.startUtc.toISOString()
    const rows: Row[] = []
    if (s.config) {
      rows.push({ name: s.config.name, size: s.config.file.size,
                  kind: 'reference', status: 'queued', done: 0,
                  file: s.config.file, startUtc: firstAt })
    }
    for (const w of s.wavs) {
      rows.push({ name: w.name, size: w.size, kind: 'audio', status: 'queued',
                  done: 0, file: w.file, startUtc: w.startUtc.toISOString() })
    }
    rowsRef.current = rows
    repaint()

    // Blocked only by the two things that would file recordings against the
    // wrong hardware. Everything else starts on its own.
    const f = s.config ? configFacts(s.config.parsed) : null
    const wrong = !!(f?.deviceId && recorderSerial &&
      f.deviceId.toLowerCase() !== recorderSerial.toLowerCase())
    if (wrong || s.sensors.length > 1) { setPhase('blocked'); return }

    await upload(rows)
  }, [recorderSerial, upload])

  const onDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault(); setOver(false)
    await take(await collect(e.dataTransfer))
  }, [take])

  const retry = useCallback(async () => {
    const again = rowsRef.current.filter((r) => r.status === 'failed')
    for (const r of again) { r.status = 'queued'; r.detail = undefined; r.done = 0 }
    repaint()
    await upload(again)
  }, [upload])

  /* ------------------------------------------------------------- counting */

  const rows = rowsRef.current
  const n = { stored: 0, already: 0, failed: 0, sent: 0, total: 0, conflicts: 0 }
  let active: Row | null = null
  for (const r of rows) {
    n.total += r.size
    if (r.status === 'stored') { n.stored += 1; n.sent += r.size }
    else if (r.status === 'already') { n.already += 1; n.sent += r.size }
    else if (r.status === 'failed') n.failed += 1
    else if (r.status === 'sending' || r.status === 'checking') {
      if (!active) active = r
      if (r.status === 'sending') n.sent += r.done
    }
    if (r.conflict) n.conflicts += 1
  }

  const facts = card?.config ? configFacts(card.config.parsed) : null
  const wrongUnit = !!(facts?.deviceId && recorderSerial &&
    facts.deviceId.toLowerCase() !== recorderSerial.toLowerCase())
  const mismatched = card?.wavs.filter((w) => w.folderMismatch).length ?? 0

  // Unfinished and failed rows first, then the newest completions. What needs
  // attention stays at the top rather than being pushed off by a thousand
  // successes.
  const shown = rows.length <= MAX_ROWS ? rows : [
    ...rows.filter((r) => r.status !== 'stored' && r.status !== 'already'),
    ...rows.filter((r) => r.status === 'stored' || r.status === 'already').slice(-MAX_ROWS),
  ].slice(0, MAX_ROWS)

  const reset = () => {
    rowsRef.current = []
    setCard(null); setErr(null); setNote(null); setPhase('idle'); repaint()
  }

  /** What is happening to this file, said in bytes wherever bytes are the
   *  answer. A word alone is not feedback on a file that takes forty minutes. */
  const say = (r: Row) => {
    if (r.detail) return r.detail
    switch (r.status) {
      case 'queued': return 'in line'
      case 'checking': return `checking ${fmtBytes(r.done)} of ${fmtBytes(r.size)}`
      case 'sending': return `uploading ${fmtBytes(r.done)} of ${fmtBytes(r.size)}`
                           + (r.rate ? ` · ${fmtBytes(r.rate)}/s` : '')
      case 'stored': return 'stored'
      case 'already': return 'already stored'
      case 'failed': return 'failed'
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      {phase === 'idle' && (
        <div
          className={`dropzone${over ? ' over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true) }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => void onDrop(e)}
        >
          <strong>Drop the card’s folders here</strong>
          <span className="faint">
            The whole card, or its <span className="mono">YYYYMMDD</span> folders.
            Uploading starts as soon as they land — there is nothing to press.
          </span>
          <button className="btn ghost sm" onClick={() => picker.current?.click()}>
            Or choose a folder
          </button>
          <input
            ref={picker} type="file" multiple hidden
            /* `webkitdirectory` is what turns the dialog into a folder chooser.
               React has no typed prop for it and TS will not accept one, so it
               is spread in as a raw attribute — React passes unknown lowercase
               attributes straight through to the DOM. `directory` alongside it
               is the standards-track spelling, harmless where unsupported. */
            {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
            onChange={(e) => {
              if (e.target.files) void take(fromPicker(e.target.files))
              e.target.value = ''
            }}
          />
        </div>
      )}

      {phase === 'scanning' && <div className="faint">Reading the card…</div>}
      {err && <div className="notice bad">{err}</div>}
      {note && <div className="notice">{note}</div>}

      {/* The two refusals, kept above the list because they are the reason it
        * is not moving. Both are about attribution, which is the one thing an
        * import cannot fix afterwards. */}
      {wrongUnit && (
        <div className="notice bad">
          This card says it came out of <span className="mono">{facts!.deviceId}</span>,
          but this deployment is <span className="mono">{recorderSerial}</span>.
          Importing would attribute these recordings to the wrong unit, so
          nothing has been uploaded.
        </div>
      )}
      {card && card.sensors.length > 1 && (
        <div className="notice bad">
          The filenames name {card.sensors.length} different units
          ({card.sensors.join(', ')}). One deployment cannot hold two units’
          recordings — import them separately.
        </div>
      )}

      {rows.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          {/* Bytes, not files. One 4 GiB recording next to seventeen small
            * ones makes "3 of 18" a number that moves in the wrong places. */}
          <div className="bar">
            <div className="bar-fill" style={{
              width: `${Math.min(100, Math.round((n.sent / Math.max(1, n.total)) * 100))}%`,
            }} />
          </div>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span className="faint">
              {fmtBytes(n.sent)} of {fmtBytes(n.total)}
              {' · '}<span className="num">{n.stored.toLocaleString()}</span> stored
              {n.already > 0 && <> · <span className="num">{n.already.toLocaleString()}</span> already there</>}
              {n.failed > 0 && <> · <span className="num">{n.failed.toLocaleString()}</span> failed</>}
              {' '}of <span className="num">{rows.length.toLocaleString()}</span> files
            </span>
            {phase === 'uploading' && (
              <button className="btn ghost sm" onClick={stop}>Stop</button>
            )}
          </div>

          <ul className="filelist">
            {shown.map((r) => (
              <li key={r.name} className={`filerow is-${r.status}`}>
                <span className="filerow-mark" aria-hidden="true" />
                <span className="mono filerow-name">{r.name}</span>
                <span className="faint filerow-note">
                  {say(r)}
                  {r.conflict && r.status !== 'failed' && ' · outside the window'}
                </span>
                <span className="faint filerow-size">{fmtBytes(r.size)}</span>
                {/* The row's own progress, under it. On a file this size the
                  * file-level bar is the only one that moves for minutes. */}
                {(r.status === 'checking' || r.status === 'sending') && (
                  <span className="filerow-bar" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, (r.done / Math.max(1, r.size)) * 100)}%` }} />
                  </span>
                )}
              </li>
            ))}
          </ul>
          {rows.length > shown.length && (
            <p className="faint">
              …and <span className="num">{(rows.length - shown.length).toLocaleString()}</span> more
              already stored.
            </p>
          )}
        </div>
      )}

      {phase === 'uploading' && (
        <span className="faint">
          Stopping is safe. What has landed stays, and dropping the same folders
          again picks up where this left off.
        </span>
      )}

      {n.conflicts > 0 && (
        <div className="notice">
          <span className="num">{n.conflicts.toLocaleString()}</span> recording(s)
          fall outside this deployment’s window. They are stored and flagged,
          never dropped — but it usually means the mission’s start or end date
          needs correcting rather than the files.
        </div>
      )}

      {mismatched > 0 && (
        <div className="notice">
          <span className="num">{mismatched}</span> file(s) sit in a folder
          whose date does not match their own name. The name is used, since it
          carries the time as well as the day.
        </div>
      )}

      {card?.config && (
        <details className="card flush">
          <summary>
            Configuration file · <span className="mono">{card.config.name}</span>
          </summary>
          <div style={{ padding: '0 16px 16px' }}>
            <dl className="dl">
              {Object.entries(card.config.parsed).map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}>
                  <dt>{k}</dt><dd className="mono">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="faint" style={{ marginTop: 10 }}>
              Uploaded with the recordings and kept against this deployment, so
              the settings survive the card being wiped.
            </p>
          </div>
        </details>
      )}

      {card && !card.config && phase !== 'idle' && (
        <div className="notice">
          No configuration file on this card. The recordings import normally;
          the unit’s settings just are not recorded anywhere, and nothing can
          check the card came out of this unit.
        </div>
      )}

      {card && card.skipped.length > 0 && (
        <details>
          <summary className="faint">{card.skipped.length} file(s) skipped</summary>
          <ul className="picklist" style={{ marginTop: 8 }}>
            {card.skipped.slice(0, 50).map((s) => (
              <li key={s.name}>
                <span className="mono">{s.name}</span> <span className="faint">— {s.why}</span>
              </li>
            ))}
          </ul>
          {card.skipped.length > 50 && (
            <p className="faint">…and {card.skipped.length - 50} more.</p>
          )}
        </details>
      )}

      {(phase === 'done' || phase === 'blocked') && (
        <div className="row">
          {n.failed > 0 && (
            <button className="btn primary" onClick={() => void retry()}>
              Retry {n.failed.toLocaleString()} failed
            </button>
          )}
          <button className="btn ghost" onClick={reset}>
            Import another card
          </button>
        </div>
      )}
    </div>
  )
}
